import { afterNextRender, ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Brand, IBrandCard, ICampaign } from '../../types';
import { backend, domain } from '../../../environments/environment';
import { AuthService } from '../../auth.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatTabsModule } from '@angular/material/tabs';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, map, switchMap, tap } from 'rxjs';
import { PerformancePipe } from '../../performance.pipe';
import { ImageFallbackDirective } from '../../image-fallback.directive';
import { LocationService } from '../../location.service';
import { BucketPipe } from '../../bucket.pipe';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { MetaService } from '../../meta.service';
import { SafePipe } from '../../safe.pipe';

export interface IPrizeDetail {
  _id: string;
  title: string;
  type: string;
  currently_active?: boolean;
  start?: string;
  end?: string;
  brand? : IBrandCard;
  logo_url?: string;
}

interface IPrizeResult {
  campaign: ICampaign;
  current : IPrizeDetail[];
  future?: IPrizeDetail[];
  giver_name?: string;
  giver_image?: string;
}

@Component({
  selector: 'campaign',
  templateUrl: './campaign.component.html',
  styleUrls: ['./campaign.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, MatButtonModule, MatDividerModule, MatTabsModule, PerformancePipe, ImageFallbackDirective, BucketPipe, SafePipe],
  host: {
    '[class.no-footer]': 'isCoke()',
    '[style]': 'campaignStyle()'
  }
})
export default class CampaignComponent {
  router = inject(LocationService);
  activated = inject(ActivatedRoute);
  sanitizer = inject(DomSanitizer);
  http = inject(HttpClient);
  toast = inject(ToastrService);
  meta = inject(MetaService);

  selectedIndex = signal<number>(0);
  animated = signal<boolean>(false);

  path = input<string>();

  loading = signal<boolean>(false);

  prizeResult = signal<IPrizeResult|null>(null);;
  animating = signal<boolean>(false);

  campaignStyle = signal<string>('');

  config = computed(() => this.prizeResult()?.campaign.configuration ?? {});

  queryParams = signal<{ [key: string]: string }>({
    path: '/home',
    url: encodeURI(domain + '/get-the-app'),
    utm_source: this.router.initialURL?.searchParams.get('utm_source') || 'cloudigo',
    utm_medium: this.router.initialURL?.searchParams.get('utm_medium') || 'web',
    store: '1',
    pv: '95'
  });

  isCoke = signal<boolean>(false);
  cokeUrl = signal<string|null>(null);

  giftLists: { key: string; value: 'current' | 'future'; }[] = [{ key: 'Now', value: 'current' }, { key: 'Coming Soon', value: 'future' }];

  constructor(){
    const currentCampaign = this.router.initialURL?.searchParams.get('utm_campaign') || 'cloudigo_coke_landing';
    this.cokeUrl.set(`https://win.mycoke.com.mt?utm_source=cloudigo&utm_medium=iframe&utm_campaign=${currentCampaign}&iframe=1`);

    this.isCoke.set(this.router.current.includes('coke') && !this.router.current.includes('quiz'));

    if(this.isCoke()){
      return;
    }

    toObservable(this.path).pipe(
      takeUntilDestroyed(),
      map(path => {
        if(path?.includes('coke') && !path.includes('quiz')){
          this.isCoke.set(true);
          return null;
        } else {
          this.isCoke.set(false);
          return path;
        }
      }),
      filter(path => !!path),
      tap(() => this.loading.set(true)),
      switchMap(path => this.http.get<IPrizeResult>(`${backend}campaign?path=${path}`))
    ).subscribe({
      next: prizeResult => {
        this.prizeResult.set(prizeResult);

        this.campaignStyle.set(Object.entries(prizeResult.campaign.configuration || {}).filter(([key]) => key.endsWith('_color')).reduce<string>((t,[key,value]) => {
          return t + `--${key}: ${value}; `;
        }, ''));
  
        this.queryParams.update(params => {
          params.path = '/d';
          params.id = `/game/${(prizeResult.campaign.trackable || '').toLowerCase()}`;
          params.utm_campaign = prizeResult.campaign.trackable
  
          return Object.assign({}, params);
        });

        this.meta.setTags(`${prizeResult.campaign.title || 'Play and win'} - cloudigo Malta`, 'Learn how to participate in this game and get the chance to win great prizes on the cloudigo app!', this.config().web_desktop_image);

        this.loading.set(false);
      },
      error: error => {
        if(this.isCoke()) return;

        this.loading.set(false);

        if(this.meta.isBrowser){
          this.toast.error('Competition ended or does not exist');
        }

        this.router.navigate(['/']);
      }
    });

  }

  swipe(direction: string){
    if(direction != 'swipeleft' && direction != 'swiperight') return;

    if(direction == 'swipeleft' && this.selectedIndex() < 2)
      this.selectedIndex.update(v => v+1);

    if(direction == 'swiperight' && this.selectedIndex() > 0)
      this.selectedIndex.update(v => v-1);

  }

}
